import { dev_cache_prefix_of } from "../dev-mode-rules.js"

type CacheEntry<T> = {
  value: T
  expiresAt: number
}

const isDev = process.env.NODE_ENV === 'development';

// Every distinct pathname that is ever requested creates a key (page-*, redirect-*),
// so the store has to be bounded or bot traffic alone will grow it forever.
const DEFAULT_MAX_ENTRIES = Number(process.env.APP_CACHE_MAX_ENTRIES) || 5000;

class MemoryCache {
  private store = new Map<string, CacheEntry<any>>()
  // Keyed by cache key, holds the in-flight factory promise so that N concurrent
  // misses trigger one upstream call instead of N (cache stampede).
  private inflight = new Map<string, Promise<any>>()
  private maxEntries: number
  // added for storefront dev mode (jument): a prefix getOrSet puts before every key, read per request; none by default
  private keyPrefix: (() => Promise<string>) | null

  constructor(maxEntries: number = DEFAULT_MAX_ENTRIES, keyPrefix: (() => Promise<string>) | null = null) {
    this.maxEntries = maxEntries
    this.keyPrefix = keyPrefix
  }

  get<T>(key: string): T | null {
    const entry = this.store.get(key)
    if (!entry) return null

    if (Date.now() > entry.expiresAt) {
      this.store.delete(key)
      return null
    }
    return entry.value
  }

  set<T>(key: string, value: T, ttlSeconds: number) {

    ttlSeconds = isDev ? 15 : ttlSeconds

    // Map iterates in insertion order, so re-inserting moves the key to the end
    // and the oldest key is always the first one prune() sees.
    this.store.delete(key)
    this.store.set(key, {
      value,
      expiresAt: Date.now() + (ttlSeconds * 1000),
    })

    if (this.store.size > this.maxEntries) {
      this.prune()
    }
  }

  /**
   * Cache-aside with stampede protection. Prefer this over get()/set() pairs:
   * when the entry expires, only the first caller runs `factory`, the rest wait
   * on the same promise. Nothing is cached if `factory` rejects.
   *
   * `ttlSeconds` may also be a function of the value (all sites): e.g. a "not found" answer kept for 30 seconds and a
   * found one for 5 minutes, without a second set() under a key the caller would have to rebuild with the dev prefix.
   */
  async getOrSet<T>(key: string, ttlSeconds: number | ((value: T) => number), factory: () => Promise<T>): Promise<T> {
    if (this.keyPrefix) {
      key = (await this.keyPrefix()) + key
    }
    const cached = this.get<T>(key)
    if (cached !== null) return cached

    const pending = this.inflight.get(key)
    if (pending) return pending as Promise<T>

    const promise = (async () => {
      try {
        const value = await factory()
        this.set(key, value, typeof ttlSeconds === "function" ? ttlSeconds(value) : ttlSeconds)
        return value
      } finally {
        this.inflight.delete(key)
      }
    })()

    this.inflight.set(key, promise)
    return promise
  }

  /** Drops expired entries, then the oldest ones until the store fits again. */
  prune() {
    const now = Date.now()
    for (const [key, entry] of this.store) {
      if (now > entry.expiresAt) this.store.delete(key)
    }

    while (this.store.size > this.maxEntries) {
      const oldest = this.store.keys().next().value
      if (oldest === undefined) break
      this.store.delete(oldest)
    }
  }

  get size(): number {
    return this.store.size
  }

  delete(key: string) {
    this.store.delete(key)
    this.inflight.delete(key)
  }

  clear() {
    this.store.clear()
    this.inflight.clear()
  }
}

/*
 * Storefront dev mode (helpers/dev-mode-rules.js): a browser in dev mode is served another site's data, so its entries
 * get their own prefix. "" for every other visitor, and outside a request (next/headers throws there). get()/set()
 * callers that hold such data add it themselves (cache-data/siteSetting.js, apis/proxy-api.js).
 */
export const dev_mode_cache_prefix = async (): Promise<string> => {
  try {
    const { cookies } = require("next/headers")
    const jar = await cookies()
    return dev_cache_prefix_of(Object.fromEntries(jar.getAll().map((cookie: { name: string, value: string }) => [cookie.name, cookie.value])))
  } catch {
    return ""
  }
}

const globalCache =
  (globalThis as any).__APP_CACHE__ ?? new MemoryCache(DEFAULT_MAX_ENTRIES, dev_mode_cache_prefix)

  ; (globalThis as any).__APP_CACHE__ = globalCache

export { MemoryCache }
export default globalCache
